import React, { useState } from "react";
import Card from "components/card";
import { MdContentCopy } from "react-icons/md";
import { toast } from "react-toastify";
import { fetchDashboardToken } from "services/apiService";

export interface SubscriptionPlan {
  name: string;
  amount_in_inr: string;
  amount_in_usd: string;
  tag_line_for_plan: string;
  interval: string;
}

export interface OrgAdmin {
  full_name: string;
  email: string;
}

export interface Organization {
  name: string;
  slug: string;
  dashboard_url?: string;
  subscription_plan: SubscriptionPlan;
  org_admin?: OrgAdmin;
  trial_end_date?: string;
}

export interface Profile {
  email: string;
  full_name: string;
  organization: Organization;
  contact_number: string;
  is_subscribed: boolean;
  date_joined: string;
}

interface InformationProps {
  profile: Profile;
}

const Row = ({ label, value, onClick }: { label: string; value: string; onClick?: () => void }) => (
  <div className="flex items-center justify-between py-3.5">
    <span className="text-sm text-[#6B7280] shrink-0 w-40">{label}</span>
    <span
      className={`text-sm font-medium text-[#1F2937] text-right break-all flex items-center gap-2 ${onClick ? "cursor-pointer group" : ""}`}
      onClick={onClick}
    >
      {value}
      {onClick && (
        <MdContentCopy className="shrink-0 text-[#9CA3AF] group-hover:text-brand-500 transition-colors" size={14} />
      )}
    </span>
  </div>
);

const Information: React.FC<InformationProps> = ({ profile }) => {
  const [goingToDashboard, setGoingToDashboard] = useState(false);

  const handleGoToDashboard = async () => {
    setGoingToDashboard(true);
    try {
      const { url, token } = await fetchDashboardToken();
      window.open(`${url}?token=${token}`, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 402) {
        toast.error("No active subscription. Please purchase a plan first.");
      } else if (status === 404) {
        toast.info("Your dashboard is being set up. You'll receive access shortly.");
      } else {
        toast.error("Could not load dashboard. Please try again.");
      }
    } finally {
      setGoingToDashboard(false);
    }
  };

  const hasSubscription = !!profile.organization?.subscription_plan;
  const trialEndDate = profile.organization?.trial_end_date
    ? new Date(profile.organization.trial_end_date) : null;
  const isOnTrial = trialEndDate !== null && trialEndDate > new Date();
  const trialDaysLeft = trialEndDate
    ? Math.max(0, Math.ceil((trialEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : 0;
  const hasAccess = hasSubscription || isOnTrial;
  const isAdmin = localStorage.getItem("isOrganizationAdmin") === "true";
  const orgAdmin = profile.organization?.org_admin;

  const noAccessMessage = isAdmin
    ? "Subscribe to access"
    : orgAdmin
      ? `Contact ${orgAdmin.full_name} (${orgAdmin.email}) for access`
      : "Contact your admin for access";

  return (
    <Card extra="w-full p-6">
      {isOnTrial && (
        <div className={`mb-4 flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium ${
          trialDaysLeft <= 2
            ? 'bg-red-50 text-red-700 border border-red-200'
            : 'bg-amber-50 text-amber-700 border border-amber-200'
        }`}>
          <span>{trialDaysLeft <= 2 ? '⚠️' : '🕐'}</span>
          <span>
            {trialDaysLeft === 0
              ? 'Your trial expires today — '
              : `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left in your trial — `}
            {isAdmin
              ? <a href="/dashboard/profile?tab=pricing" className="underline font-semibold">Subscribe now</a>
              : 'Subscribe before your trial ends to keep access.'}
          </span>
        </div>
      )}
      <h4 className="text-base font-semibold text-[#1F2937] mb-1">Profile Information</h4>
      <p className="text-xs text-[#6B7280] mb-4">Your current account details</p>

      <div className="divide-y divide-[#E4E7EB]">
        <Row label="Full Name" value={profile.full_name || "—"} />
        <Row label="Email" value={profile.email || "—"} />
        <Row label="Contact Number" value={profile.contact_number || "—"} />
      </div>

      <h4 className="text-base font-semibold text-[#1F2937] mt-6 mb-1">Organisation</h4>
      <p className="text-xs text-[#6B7280] mb-4">Your linked organisation details</p>

      <div className="divide-y divide-[#E4E7EB]">
        <Row label="Organisation Name" value={profile.organization?.name || "—"} />
        {profile.organization?.dashboard_url && (
          <div className="flex items-center justify-between py-3.5">
            <span className="text-sm text-[#6B7280] shrink-0 w-40">Your Dashboard</span>
            {hasAccess ? (
              <button
                onClick={handleGoToDashboard}
                disabled={goingToDashboard}
                className="text-sm font-medium text-brand-500 hover:text-brand-600 transition-colors disabled:opacity-60"
              >
                {goingToDashboard
                  ? "Loading…"
                  : `${profile.organization.dashboard_url.replace("https://", "")} →`}
              </button>
            ) : (
              <span className="text-sm text-[#6B7280] text-right">{noAccessMessage}</span>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default Information;
